/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-09-23 11:39:10
 *
 *******************************************************/
// @flow
//
import _ from 'lodash';
import React, { Component } from 'react';
import { Page, Layout, Card, SkeletonDisplayText, SkeletonBodyText } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { connect } from 'react-redux';

import Rules from './rules';
import MainMenu from 'src/components/containers/main-menu';
import ExceededPlanNotification from 'src/components/containers/common/exceeded-plan-notifications';
import { RenderSelect2 } from 'src/components/views/common/render-select-2';

import * as MergeValuesActions from 'actions/merge-values-actions';
import * as FilterDataActions from 'actions/filter-data-actions';
import * as checklistActions from 'actions/dashboard/checklist-actions';
import { APP_LANGUAGE_ENGLISH_VALUE } from 'src/constants/language-setting';
import { getAvailableTypes, getTypeInformation } from 'src/helpers/merge-values-helper';

import { routes } from 'src/routes';

type Props = WithI18nProps;
type State = {
  optionTypes: Array<{ value: string, label: string }>,
  type?: { value: string, label: string }
};
class MergeValues extends Component<Props, State> {
  state = {
    optionTypes: [],
    type: undefined
  };

  componentDidMount() {
    const {
      options,
      getFilterData,
      getTypesSuccess,
      getMergeValuesTypes,
      getMergeValuesByType,
      appLanguage,
      i18n
    } = this.props;
    const { type: typeValue } = this.props.match.params;

    getFilterData();

    if (!_.isEmpty(typeValue)) {
      // Edit page
      getMergeValuesByType(typeValue);
      const type = getTypeInformation(typeValue, options, appLanguage, i18n);
      this.setState({ type });
    } else {
      // Add new page: getting exist merge values type and list of merge value
      if (!getTypesSuccess) {
        getMergeValuesTypes();
      }
    }
  }

  componentWillUnmount() {
    const { resetMergeValuesItem } = this.props;
    resetMergeValuesItem();
  }

  componentWillReceiveProps(nextProps: Props) {
    const { loadingFilterData, appLanguage, i18n } = this.props;
    const { loadingFilterData: nextLoadingFilterData, options: nextOptions } = nextProps;
    const { type: typeValue } = this.props.match.params;

    // Check when the modal is loaded before API finish
    if (loadingFilterData !== nextLoadingFilterData && nextLoadingFilterData === false) {
      let optionTypes = getAvailableTypes(nextOptions, appLanguage, i18n);
      optionTypes = this.removeExistingOptionTypes(optionTypes);

      // Get option original name
      const type = getTypeInformation(typeValue, nextOptions, appLanguage, i18n);

      this.setState({ optionTypes, type });
    }
  }

  render() {
    const { loading, rules, i18n, loadingFilterData } = this.props;
    const { optionTypes, type } = this.state;
    const { type: typeValue } = this.props.match.params;

    const title = _.isEmpty(type)
      ? i18n.translate('AdminHTML.MergeValues.Item.pageTitleAdd')
      : loading
      ? i18n.translate('AdminHTML.MergeValues.Item.pageTitleEdit')
      : //$FlowFixMe
        type.label;
    const isDisabledType = !_.isEmpty(rules) || !_.isEmpty(typeValue) || loadingFilterData;

    return (
      <div className="BC-Page BC-Page__Merge-Value">
        <MainMenu menuId="menu-filters-merge-values" />
        <Page
          title={title}
          breadcrumbs={[
            {
              content: i18n.translate('AdminHTML.MergeValues.MergeValues.pageTitle'),
              onAction: this.backToManagementPage
            }
          ]}>
          <Layout>
            <Layout.Section>
              <div className="BC-Container__Notifications">
                {/* Sticky notification for exceeded plan */}
                <ExceededPlanNotification />
              </div>
            </Layout.Section>
            <Layout.Section>
              <Card title={i18n.translate('AdminHTML.MergeValues.Item.mergedType')}>
                <Card.Section>
                  {loading ? (
                    <div>
                      <SkeletonDisplayText size="small" />
                      <SkeletonBodyText lines={1} />
                    </div>
                  ) : (
                    <div>
                      <RenderSelect2
                        name="type"
                        label={i18n.translate('AdminHTML.MergeValues.Item.mergedType_Label')}
                        options={optionTypes}
                        placeholder={i18n.translate(
                          'AdminHTML.MergeValues.Item.mergedType_Placeholder'
                        )}
                        value={type}
                        onChange={this.changeType}
                        isDisabled={isDisabledType}
                      />
                    </div>
                  )}
                </Card.Section>
              </Card>
            </Layout.Section>
            <Layout.Section>
              <Rules type={type ? type.value : ''} loading={loading} rules={rules} />
            </Layout.Section>
          </Layout>
        </Page>
      </div>
    );
  }

  backToManagementPage = () => {
    const { history } = this.props;
    history.push(routes.mergeValues.view());
  };

  removeExistingOptionTypes(optionTypes) {
    const { types } = this.props;
    const { type = {} } = this.state;
    const { value = '' } = type;

    let result = [...optionTypes];

    if (!_.isEmpty(types)) {
      types.map(item => {
        if (item.type !== value) {
          _.remove(result, i => i.value === item.type);
        }
        return '';
      });
    }

    return result;
  }

  changeType = (type: Object) => {
    this.setState({ type });
  };
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  getFilterData: () => dispatch(FilterDataActions.getFilterData(ownProps.i18n)),
  getMergeValuesTypes: () => dispatch(MergeValuesActions.getMergeValuesTypes(ownProps.i18n)),
  getMergeValuesByType: (type: string) =>
    dispatch(MergeValuesActions.getMergeValuesByType(type, ownProps.i18n)),
  resetMergeValuesItem: () => dispatch(MergeValuesActions.resetMergeValuesItem()),
  getChecklist: () => dispatch(checklistActions.getChecklist(ownProps.i18n))
});

const mapStateToProps = (state, props) => {
  const rules = _.get(state.mergeValues, 'rules', {});
  const types = _.get(state.mergeValues, 'types', []);
  const appLanguage = _.get(state.oauth.tenantApp, 'appLanguage', APP_LANGUAGE_ENGLISH_VALUE);

  return {
    loadingFilterData: _.get(state.filterData, 'loading', false),
    getTypesSuccess: _.get(state.mergeValues, 'getTypesSuccess', false),
    options: _.get(state.filterData, 'filterData.optionsList', []),
    appLanguage,
    rules,
    types
  };
};
export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(MergeValues));
